import React, {useMemo} from 'react';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {Box, IconButton} from '@mui/material';
import {GridColDef} from '@mui/x-data-grid-pro';
import {SearchResults} from '../../components/Common/SearchResults';
import {getCurrencyString} from '../../utils/currencyFormatter';
import {AmlKycContact} from '../../schema-types/AmlKycContact';
import {AmlKycOptions_amlKycOptions_nationalities} from '../../schema-types/AmlKycOptions';

type Props = {
  contacts: AmlKycContact[];
  nationalities: AmlKycOptions_amlKycOptions_nationalities[];
  onEdit: (contactId: string) => void;
};

const BLANK = '-';

export const AML_KYC_COLUMNS: {
  key: keyof AmlKycContact;
  label: string;
}[] = [
  {key: 'nationality', label: 'Nationality'},
  {key: 'dualNationality', label: 'Dual nationality'},
  {key: 'employmentStatus', label: 'Employment status'},
  {key: 'hubwiseOccupation', label: 'Occupation'},
  {key: 'hubwiseIndustry', label: 'Industry'},
  {key: 'annualIncome', label: 'Annual income'},
  {key: 'retirementAge', label: 'Retirement age'},
];

// Curo stores the ISO code; show the matching Curo label, falling back to the raw code if the list has moved on.
const displayNationality = (
  code: string | null,
  nationalities: AmlKycOptions_amlKycOptions_nationalities[]
) => {
  if (!code) {
    return BLANK;
  }
  return nationalities.find((nationality) => nationality.code === code)?.label ?? code;
};

const displayIncome = (annualIncome: string | null) =>
  annualIncome === null ? BLANK : getCurrencyString(annualIncome, {decimalPlaces: 0});

const displayCell = (
  contact: AmlKycContact,
  column: typeof AML_KYC_COLUMNS[number],
  nationalities: AmlKycOptions_amlKycOptions_nationalities[]
) => {
  if (column.key === 'nationality' || column.key === 'dualNationality') {
    return displayNationality(contact[column.key], nationalities);
  }
  if (column.key === 'annualIncome') {
    return displayIncome(contact.annualIncome);
  }
  if (column.key === 'retirementAge') {
    return contact.retirementAge === null ? BLANK : `${contact.retirementAge}`;
  }
  return contact[column.key] || BLANK;
};

const contactName = (contact: AmlKycContact) => contact.simplifiedName || 'Contact';

/**
 * Account overview AML/KYC table (DBO-1584): one row per primary/secondary
 * contact, mirroring the fields captured in Curo, with an edit button per row.
 */
export const AmlKycTable: React.FC<Props> = ({contacts, nationalities, onEdit}) => {
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'simplifiedName',
        sortable: false,
        headerName: 'Name',
        valueGetter: (value, row) => contactName(row as AmlKycContact),
        flex: 1,
      },
      ...AML_KYC_COLUMNS.map(
        (column): GridColDef => ({
          field: column.key,
          sortable: false,
          headerName: column.label,
          valueGetter: (value, row) =>
            displayCell(row as AmlKycContact, column, nationalities),
          flex: 1,
        })
      ),
      {
        field: 'edit',
        sortable: false,
        headerName: '',
        width: 60,
        renderCell: (params) => {
          const contact = params.row as AmlKycContact;
          return (
            <Box display="flex" alignItems="center" height="100%">
              <IconButton
                size="small"
                color="primary"
                aria-label={`Edit AML/KYC details for ${contactName(contact)}`}
                onClick={() => onEdit(contact.id)}
              >
                <EditOutlinedIcon />
              </IconButton>
            </Box>
          );
        },
      },
    ],
    [nationalities, onEdit]
  );

  return (
    <SearchResults
      dataGridId="AccountAmlKyc"
      title="AML/KYC"
      alternateRowBg
      search=""
      hideFooter
      sx={{
        '& *': {
          textAlign: 'left',
          '-ms-overflow-style': 'none',
        },
        '& ::-webkit-scrollbar': {
          width: '0 !important',
        },
      }}
      data={contacts}
      columns={columns}
      minHeight={false}
      rowHeight={40}
      onRowDoubleClick={() => {}}
    />
  );
};
